import type { Dispatch, ReactNode, SetStateAction } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type {
  ModelExpertModifiedConfigResponse,
  ModifiedConfigChange,
} from "@citrine/resources/catalyst";
import type { IGraphMLPredictorConfig } from "@citrine/resources/predictor";
import type { CatalystMode } from "@citrine/types/full-story";

export type { CatalystMode };

export interface CatalystPredictorState {
  config: IGraphMLPredictorConfig;
  hasWritePermissions: boolean;
  id?: string;
  isValidatingOrEvaluating: boolean;
  version?: string;
  updatePredictor: (
    modifiedConfig: ModelExpertModifiedConfigResponse,
  ) => Promise<void>;
}

interface ObservableEvent {
  expanded: (newExpanded: boolean, prevExpanded: boolean) => void;
  mode: (newMode: CatalystMode, prevMode: CatalystMode) => void;
  predictor: (
    newPredictorState: CatalystPredictorState | undefined,
    prevPredictorState: CatalystPredictorState | undefined,
  ) => void;
}

interface CatalystState {
  agentEnabled: boolean;
  changes?: ModifiedConfigChange[];
  expanded: boolean;
  mode: CatalystMode;
  modelExpertEnabled: boolean;
  /** Apex optimization run ID for the current candidates page, surfaced to agents to scope actions. */
  optimizationRunId?: string;
  predictor?: CatalystPredictorState;
  setAgentEnabled: Dispatch<SetStateAction<boolean>>;
  setChanges: Dispatch<SetStateAction<ModifiedConfigChange[] | undefined>>;
  setExpanded: Dispatch<SetStateAction<boolean>>;
  /** Tracks the last explicit mode selection that a user made (not an automatic mode change) */
  setMode: (mode: CatalystMode) => void;
  setModelExpertEnabled: Dispatch<SetStateAction<boolean>>;
  setOptimizationRunId: Dispatch<SetStateAction<string | undefined>>;
  setPredictor: Dispatch<SetStateAction<CatalystPredictorState | undefined>>;
  subscribe: <Event extends keyof ObservableEvent>(
    event: Event,
    callback: ObservableEvent[Event],
  ) => () => void;
}

const catalystContext = createContext<CatalystState>({
  agentEnabled: false,
  expanded: false,
  mode: "literature-expert",
  modelExpertEnabled: false,
  setAgentEnabled: () => {},
  setChanges: () => {},
  setExpanded: () => {},
  setMode: () => {},
  setModelExpertEnabled: () => {},
  setOptimizationRunId: () => {},
  setPredictor: () => {},
  subscribe: () => () => {},
});

export const useCatalystContext = () => useContext(catalystContext);

/**
 * The `modelExpertEnabled` prop allows us to override the state in unit tests
 * and not have to rely on routing to test the Model Expert.
 *
 * `initialMode` is also exposed for testing purposes.
 **/
export function CatalystProvider({
  agentEnabled: agentEnabledProp = false,
  children,
  initialMode,
  modelExpertEnabled: modelExpertEnabledProp = false,
}: {
  agentEnabled?: boolean;
  children: ReactNode;
  initialMode?: CatalystMode;
  modelExpertEnabled?: boolean;
}) {
  const [agentEnabled, setAgentEnabled] = useState(agentEnabledProp);
  const [modelExpertEnabled, setModelExpertEnabled] = useState(
    modelExpertEnabledProp,
  );
  const [optimizationRunId, setOptimizationRunId] = useState<string>();

  const observers = useRef<
    Partial<{
      [K in keyof ObservableEvent]: Set<ObservableEvent[K]>;
    }>
  >({});
  const subscribe = useCallback<CatalystState["subscribe"]>(
    (event, callback) => {
      observers.current[event] ||= new Set<any>();
      observers.current[event]!.add(callback);
      return () => observers.current[event]!.delete(callback);
    },
    [],
  );

  const [expanded, setExpanded] = useState(false);
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  useEffect(() => {
    const observersRef = observers;
    return () => {
      observersRef.current.expanded?.forEach((fn) => {
        fn(expandedRef.current, expanded);
      });
    };
  }, [expanded]);

  const [selectedMode, setSelectedMode] = useState(initialMode);

  const isModeEnabled: Record<CatalystMode, boolean> = {
    agent: agentEnabled,
    "literature-expert": true, // always available
    "model-expert": modelExpertEnabled,
  };

  // Default to agent when available, else literature-expert, until the user picks.
  const modeSelection =
    selectedMode ?? (agentEnabled ? "agent" : "literature-expert");

  const mode = isModeEnabled[modeSelection]
    ? modeSelection
    : "literature-expert";

  const modeRef = useRef(mode);
  modeRef.current = mode;
  useEffect(() => {
    const observersRef = observers;
    return () => {
      observersRef.current.mode?.forEach((fn) => {
        fn(modeRef.current, mode);
      });
    };
  }, [mode]);

  const [predictor, setPredictor] = useState<CatalystPredictorState>();
  const predictorRef = useRef(predictor);
  predictorRef.current = predictor;
  useEffect(() => {
    const observersRef = observers;
    return () => {
      observersRef.current.predictor?.forEach((fn) => {
        fn(predictorRef.current, predictor);
      });
    };
  }, [predictor]);

  const [changes, setChanges] = useState<ModifiedConfigChange[]>();

  const contextValue = useMemo<CatalystState>(
    () => ({
      agentEnabled,
      expanded,
      mode,
      modelExpertEnabled,
      changes,
      optimizationRunId,
      predictor,
      setAgentEnabled,
      setExpanded,
      setMode: setSelectedMode,
      setModelExpertEnabled,
      setChanges,
      setOptimizationRunId,
      setPredictor,
      subscribe,
    }),
    [
      agentEnabled,
      expanded,
      mode,
      modelExpertEnabled,
      changes,
      optimizationRunId,
      predictor,
      setExpanded,
      setPredictor,
      setSelectedMode,
      subscribe,
    ],
  );

  return (
    <catalystContext.Provider value={contextValue}>
      {children}
    </catalystContext.Provider>
  );
}
